import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription, catchError, finalize, of, tap } from 'rxjs';

@Component({
	selector: 'app-invite-candidate-dialog-box',
	standalone: true,
	imports: [CommonModule, FormsModule, MatProgressSpinnerModule, MatTooltipModule],
	templateUrl: './invite-candidate-dialog-box.component.html',
	styleUrl: './invite-candidate-dialog-box.component.scss'
})
export class InviteCandidateDialogBoxComponent implements OnDestroy {

	invitedEmailList: string[] = [];
	existingEmails: string[] = [];
	invitedEmailsInput = '';
	isLoading = false;
	subscription = new Subscription();

	constructor(
		private dialogRef: MatDialogRef<InviteCandidateDialogBoxComponent>,
		@Inject(MAT_DIALOG_DATA) public data: { linkId: string, existingEmails?: string[] },
		private quizService: QuizManagementService,
		private toastr: ToastrService
	) {
		if (data?.existingEmails && Array.isArray(data.existingEmails)) {
			this.existingEmails = [...data.existingEmails];
		}
	}

	closeDialog() {
		this.dialogRef.close();
	}

	addEmails() {
		const value = this.invitedEmailsInput || '';
		const emails = value.split(/[\s,]+/).map((e: string) => e.trim()).filter((e: string) => e);
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		const validEmails = emails.filter((email: string) => emailRegex.test(email));
		const invalidEmails = emails.filter((email: string) => !emailRegex.test(email));

		if (invalidEmails.length > 0) {
			this.toastr.error(`Invalid email(s): ${invalidEmails.join(', ')}`);
			return;
		}

		if (validEmails.length > 0) {
			const uniqueNewEmails = validEmails.filter(email => !this.existingEmails.includes(email));
			const duplicates = validEmails.filter(email => this.existingEmails.includes(email));

			if (duplicates.length > 0) {
				this.toastr.info(`${duplicates.join(', ')} already invited`);
			}

			if (uniqueNewEmails.length > 0) {
				this.invitedEmailList = [...new Set([...this.invitedEmailList, ...uniqueNewEmails])];
			}
			this.invitedEmailsInput = '';
		}
	}

	removeEmail(email: string) {
		this.invitedEmailList = this.invitedEmailList.filter(e => e !== email);
	}

	sendInvite() {
		if (this.invitedEmailList.length === 0) {
			this.toastr.warning('Please add at least one candidate email address');
			return;
		}

		this.isLoading = true;
		const combinedEmails = [...this.existingEmails, ...this.invitedEmailList];
		this.subscription.add(
			this.quizService.inviteCandidatesToLink(this.data.linkId, combinedEmails).pipe(
				tap(response => {
					if (response.Success) {
						this.toastr.success(response.Message || 'Invitations sent successfully');
						this.dialogRef.close(true);
					} else {
						this.toastr.error(response.Message || 'Failed to send invitations');
					}
				}),
				catchError(() => {
					this.toastr.error('Something went wrong. Please try again');
					return of(null);
				}),
				finalize(() => {
					this.isLoading = false;
				})
			).subscribe()
		);
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}
